import { monthEnd, monthStart } from "../../../utils/methods/calendarLayout";
import type { FinanceStatus } from "../../../api/financialDashboard";
import { SEVERITY_COLORS, THEME } from "../overview/boardStyles";

export const FINANCE_STATUS_OPTIONS: Record<FinanceStatus, { label: string; color: string }> = {
  no_invoice: { label: "No final invoice", color: SEVERITY_COLORS.warning },
  unconfirmed: { label: "No send recorded", color: THEME.pink },
  queued: { label: "Queued / retrying", color: SEVERITY_COLORS.info },
  failed: { label: "Send failed", color: SEVERITY_COLORS.critical },
  comped: { label: "Comped · no send required", color: SEVERITY_COLORS.info },
  sent: { label: "Send recorded", color: THEME.green },
};
export type FinanceControls = { from: string; to: string; status: FinanceStatus | "all"; archivedOnly: boolean };
export const defaultFinanceControls = (): FinanceControls => ({ ...financeMonthRange(currentFinanceMonth()), status: "all", archivedOnly: false });

/** Saved preferences never pin dates, and never accept permissions or arbitrary query fields. */
export function financePreferences(value: unknown): Pick<FinanceControls, "status" | "archivedOnly"> {
  const input = (value ?? {}) as Record<string, unknown>;
  const status = typeof input.status === "string" && Object.prototype.hasOwnProperty.call(FINANCE_STATUS_OPTIONS, input.status)
    ? input.status as FinanceStatus : "all";
  return { status, archivedOnly: input.archivedOnly === true };
}

/** The initial month follows the same company calendar as the finance API. */
export function currentFinanceMonth(now = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Chicago", year: "numeric", month: "2-digit",
  }).formatToParts(now);
  const value = (key: string) => parts.find(part => part.type === key)?.value;
  return `${value("year")}-${value("month")}`;
}

/** A full calendar month is the initial range and the This Month shortcut. */
export function financeMonthRange(month: string) {
  return { from: monthStart(month), to: monthEnd(month) };
}

/** Mirror the API's range validation for immediate feedback; the server still enforces it. */
export function financeDateRangeError(from: string, to: string): string {
  const valid = (day: string) => /^\d{4}-\d{2}-\d{2}$/.test(day) && Number.isFinite(Date.parse(day)) &&
    new Date(day).toISOString().slice(0, 10) === day;
  if (!valid(from) || !valid(to)) return "Choose valid start and end dates.";
  if (from > to) return "The end date must be on or after the start date.";
  if ((Date.parse(to) - Date.parse(from)) / 86400000 > 365) return "Choose a date range of at most 366 days.";
  return "";
}
